import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, uniqueChannelTopic } from '@/lib/supabase';
import {
  fetchUserGroups,
  fetchArchivedGroups,
  fetchGroupDetail,
  createGroup,
  updateGroup,
  joinGroupByInviteCode,
  regenerateInviteCode,
  leaveGroup,
  deleteGroup,
  setGroupArchived,
  reorderGroups,
} from '@/api/groups';
import type { GroupWithMembers } from '@/api/groups';
import type { CreateGroupData, UpdateGroupData } from '@sharecost/shared';
import { Analytics } from '@/services/analytics.service';

/**
 * Global realtime subscription for group membership changes.
 * Invalidates the groups query when the current user is added/removed from any group.
 */
export function useGroupsRealtimeSubscription() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(uniqueChannelTopic(`group-members-${user.id}`))
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'group_members', filter: `user_id=eq.${user.id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['groups'] });
          queryClient.invalidateQueries({ queryKey: ['archived-groups'] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
}

export function useGroups() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['groups', user?.id],
    queryFn: () => fetchUserGroups(user!.id),
    enabled: !!user,
  });
}

/** Groups the current user has archived (for the "Archived" section / unarchive). */
export function useArchivedGroups() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['archived-groups', user?.id],
    queryFn: () => fetchArchivedGroups(user!.id),
    enabled: !!user,
  });
}

export function useSetGroupArchived() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({ groupId, archived }: { groupId: string; archived: boolean }) =>
      setGroupArchived(groupId, user!.id, archived),
    onSuccess: (_, { groupId, archived }) => {
      // Archiving moves a group between the active list and the archived list, and
      // changes the scope of every cross-group surface — refresh them all.
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['archived-groups'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: ['all-expenses'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      Analytics.track({
        name: archived ? 'Group Archived' : 'Group Unarchived',
        properties: { group_id: groupId },
      });
    },
  });
}

export function useGroupDetail(groupId: string | undefined) {
  return useQuery({
    queryKey: ['group', groupId],
    queryFn: () => fetchGroupDetail(groupId!),
    enabled: !!groupId,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (data: CreateGroupData) => createGroup(data, user!.id),
    onSuccess: (group, data) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      Analytics.track({
        name: 'Group Created',
        properties: { group_id: group.id, member_count: 1, currency: data.currency_code },
      });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: UpdateGroupData }) =>
      updateGroup(groupId, data),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groupBudget', groupId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
    },
  });
}

export function useJoinGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteCode: string) => joinGroupByInviteCode(inviteCode),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      Analytics.track({
        name: 'Group Joined',
        properties: { group_id: result.group_id, via: 'code' },
      });
    },
  });
}

export function useRegenerateInviteCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      expiresInDays = null,
      maxUses = null,
    }: {
      groupId: string;
      expiresInDays?: number | null;
      maxUses?: number | null;
    }) => regenerateInviteCode(groupId, expiresInDays, maxUses),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useLeaveGroup() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (groupId: string) => leaveGroup(groupId, user!.id),
    onSuccess: (_, groupId) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      Analytics.track({ name: 'Group Left', properties: { group_id: groupId } });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => deleteGroup(groupId),
    onSuccess: (_, groupId) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      Analytics.track({ name: 'Group Deleted', properties: { group_id: groupId, had_expenses: false } });
    },
  });
}

export function useReorderGroups() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (groupOrders: Array<{ groupId: string; displayOrder: number }>) =>
      reorderGroups(user!.id, groupOrders),
    onMutate: async (groupOrders) => {
      await queryClient.cancelQueries({ queryKey: ['groups', user?.id] });
      const previous = queryClient.getQueryData<GroupWithMembers[]>(['groups', user?.id]);

      queryClient.setQueryData<GroupWithMembers[]>(['groups', user?.id], old => {
        if (!old) return old;
        const orderMap = new Map(groupOrders.map(o => [o.groupId, o.displayOrder]));
        return old.map(g => ({
          ...g,
          members: g.members.map(m =>
            m.user_id === user!.id
              ? { ...m, display_order: orderMap.get(g.id) ?? m.display_order }
              : m
          ),
        }));
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['groups', user?.id], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}
